import type { Suit, MissionInstance } from './types';
import type { ScoringRule } from './scoring';

// A Mission is always SUIT-BOUND: it ships on the Mission card of a drafted
// suit, so its scoring rule references that exact suit (kind ∈ seed_count /
// seed_resources / specific_card / seed_high_count / seed_low_count). Generic,
// suit-agnostic goals live in Locations instead (see locations.ts).
//
// 24 missions, exactly 3 per suit (MISSIONS_PER_SUIT). All are "forward" goals
// (reward collecting / playing the suit) — never reverse — because the player
// who receives the Mission card is the one who drafted that suit. "Avoid X"
// goals would be incoherent here; they belong to Locations, where they apply
// symmetrically to both players regardless of what was drafted.
interface MissionTemplate {
  id: string;
  suit: Suit;
  rule: ScoringRule;     // suit already baked in (rule.seed === suit)
  name: string;          // suit baked in, ends with "(<suit>)" for disambiguation
  fluff: string;
  winScore: 1 | 2;
}

export const MISSIONS_PER_SUIT = 3;

// 24 templates — 3 per suit. Each suit's trio mixes "volume" goals (seed_count /
// seed_resources), a "trophy" goal (specific_card) and "tier" goals
// (seed_high_count / seed_low_count) so suits feel mechanically distinct while
// every mission stays thematically coherent with playing that suit.
export const MISSION_DATABASE: MissionTemplate[] = [
  // --- drug — push product, volume and grade ---
  {
    id: 'm_drug_1',
    suit: 'drug',
    rule: { kind: 'seed_count', seed: 'drug' },
    name: 'Street Saturation (drug)',
    fluff: 'Push more drug cards onto the board than your rival. Flood the corners.',
    winScore: 1,
  },
  {
    id: 'm_drug_2',
    suit: 'drug',
    rule: { kind: 'seed_resources', seed: 'drug' },
    name: 'The Big Cut (drug)',
    fluff: 'Walk away with the highest total product value — quantity and purity both count.',
    winScore: 2,
  },
  {
    id: 'm_drug_3',
    suit: 'drug',
    rule: { kind: 'seed_high_count', seed: 'drug', value: 4 },
    name: 'Premium Grade (drug)',
    fluff: 'Collect the most high-grade drug cards — value 4 and above.',
    winScore: 1,
  },

  // --- police — run the corrupt-cop network ---
  {
    id: 'm_police_1',
    suit: 'police',
    rule: { kind: 'seed_count', seed: 'police' },
    name: 'Blue Payroll (police)',
    fluff: 'Put more cops on your payroll than the rival — most police cards wins.',
    winScore: 1,
  },
  {
    id: 'm_police_2',
    suit: 'police',
    rule: { kind: 'specific_card', seed: 'police', value: 6 },
    name: 'The Commissioner (police)',
    fluff: 'Bag the 6 of police. The top badge answers to you now.',
    winScore: 2,
  },
  {
    id: 'm_police_3',
    suit: 'police',
    rule: { kind: 'seed_low_count', seed: 'police', value: 3 },
    name: 'Beat Cops (police)',
    fluff: 'Collect the most low-rank police cards — value 3 and below.',
    winScore: 1,
  },

  // --- money — the haul, every bill counts ---
  {
    id: 'm_money_1',
    suit: 'money',
    rule: { kind: 'seed_resources', seed: 'money' },
    name: 'Follow the Money (money)',
    fluff: 'End with the highest total cash value. Every bill counts.',
    winScore: 2,
  },
  {
    id: 'm_money_2',
    suit: 'money',
    rule: { kind: 'seed_count', seed: 'money' },
    name: 'Counting House (money)',
    fluff: 'Stack more money cards in your pile than your rival.',
    winScore: 1,
  },
  {
    id: 'm_money_3',
    suit: 'money',
    rule: { kind: 'seed_high_count', seed: 'money', value: 4 },
    name: 'Big Bills (money)',
    fluff: 'Collect the most large money cards — value 4 and above.',
    winScore: 1,
  },

  // --- cars — the getaway fleet ---
  {
    id: 'm_cars_1',
    suit: 'cars',
    rule: { kind: 'seed_count', seed: 'cars' },
    name: 'Full Garage (cars)',
    fluff: 'Park more car cards in your pile than the rival.',
    winScore: 1,
  },
  {
    id: 'm_cars_2',
    suit: 'cars',
    rule: { kind: 'specific_card', seed: 'cars', value: 6 },
    name: 'The Supercar (cars)',
    fluff: 'Bag the 6 of cars — the ride everyone on the strip wants.',
    winScore: 2,
  },
  {
    id: 'm_cars_3',
    suit: 'cars',
    rule: { kind: 'seed_high_count', seed: 'cars', value: 4 },
    name: 'Muscle Fleet (cars)',
    fluff: 'Collect the most high-end rides — value 4 and above.',
    winScore: 1,
  },

  // --- jewels — the shiny score ---
  {
    id: 'm_jewels_1',
    suit: 'jewels',
    rule: { kind: 'specific_card', seed: 'jewels', value: 6 },
    name: 'The Crown Jewel (jewels)',
    fluff: 'Bag the 6 of jewels. The centrepiece of the whole heist.',
    winScore: 2,
  },
  {
    id: 'm_jewels_2',
    suit: 'jewels',
    rule: { kind: 'seed_resources', seed: 'jewels' },
    name: 'Ice Cold Haul (jewels)',
    fluff: 'End with the highest total ice value extracted.',
    winScore: 1,
  },
  {
    id: 'm_jewels_3',
    suit: 'jewels',
    rule: { kind: 'seed_high_count', seed: 'jewels', value: 4 },
    name: 'Flawless Stones (jewels)',
    fluff: 'Collect the most premium jewels — value 4 and above.',
    winScore: 1,
  },

  // --- casino chips — the table, the house edge ---
  {
    id: 'm_chips_1',
    suit: 'casino chips',
    rule: { kind: 'seed_count', seed: 'casino chips' },
    name: 'Stack the Table (casino chips)',
    fluff: 'Pile up more chip cards than the rival.',
    winScore: 1,
  },
  {
    id: 'm_chips_2',
    suit: 'casino chips',
    rule: { kind: 'seed_resources', seed: 'casino chips' },
    name: 'House Edge (casino chips)',
    fluff: 'End with the highest total chip value. The house always wins.',
    winScore: 2,
  },
  {
    id: 'm_chips_3',
    suit: 'casino chips',
    rule: { kind: 'seed_low_count', seed: 'casino chips', value: 3 },
    name: 'Penny Slots (casino chips)',
    fluff: 'Collect the most low-denomination chips — value 3 and below.',
    winScore: 1,
  },

  // --- criminal contacts — the network, the crew ---
  {
    id: 'm_contacts_1',
    suit: 'criminal contacts',
    rule: { kind: 'seed_count', seed: 'criminal contacts' },
    name: 'Wide Network (criminal contacts)',
    fluff: 'Know more people — collect the most contact cards.',
    winScore: 1,
  },
  {
    id: 'm_contacts_2',
    suit: 'criminal contacts',
    rule: { kind: 'seed_high_count', seed: 'criminal contacts', value: 4 },
    name: 'Made Men (criminal contacts)',
    fluff: 'Collect the most high-rank contacts — value 4 and above.',
    winScore: 2,
  },
  {
    id: 'm_contacts_3',
    suit: 'criminal contacts',
    rule: { kind: 'seed_resources', seed: 'criminal contacts' },
    name: 'The Inner Circle (criminal contacts)',
    fluff: 'End with the highest total connect value.',
    winScore: 1,
  },

  // --- weapons — the arsenal, firepower ---
  {
    id: 'm_weapons_1',
    suit: 'weapons',
    rule: { kind: 'seed_count', seed: 'weapons' },
    name: 'Stocked Arsenal (weapons)',
    fluff: 'Hoard more weapon cards than your rival.',
    winScore: 1,
  },
  {
    id: 'm_weapons_2',
    suit: 'weapons',
    rule: { kind: 'specific_card', seed: 'weapons', value: 6 },
    name: 'The Heavy Iron (weapons)',
    fluff: 'Bag the 6 of weapons — the biggest gun on the table.',
    winScore: 2,
  },
  {
    id: 'm_weapons_3',
    suit: 'weapons',
    rule: { kind: 'seed_high_count', seed: 'weapons', value: 4 },
    name: 'Heavy Caliber (weapons)',
    fluff: 'Collect the most high-caliber weapons — value 4 and above.',
    winScore: 1,
  },
];

// Pick one of the 3 missions of the given suit, parameterized into a concrete
// MissionInstance ready to embed on that suit's Mission card.
// If `usedTemplates` is provided, prefer a template not already used this match
// so two Mission cards on the same suit (both players drafted it) don't carry
// the identical goal. With 3 templates per suit and at most 2 picks per suit,
// the constraint is always satisfiable.
export function instantiateMission(
  suit: Suit,
  prng: () => number,
  idSuffix: string,
  usedTemplates?: Set<string>
): MissionInstance {
  const all = MISSION_DATABASE.filter(t => t.suit === suit);
  const fresh = usedTemplates ? all.filter(t => !usedTemplates.has(t.id)) : all;
  const pool = fresh.length > 0 ? fresh : all;
  const tmpl = pool[Math.floor(prng() * pool.length)];
  return {
    id: `mission_${idSuffix}_${suit}`,
    templateId: tmpl.id,
    name: tmpl.name,
    fluff: tmpl.fluff,
    rule: tmpl.rule,
    winScore: tmpl.winScore,
  };
}
